import React, { useState, useEffect } from 'react';
import { Notification } from './components/Notification/Notification';
import { Login } from './components/Auth/Login';
// TalkToTally V1 removed from UI
import TalkToTallyV2Project from './components/TalkToTallyV2Project/TalkToTallyV2Project';
import SkillsHierarchyManager from './components/SkillsHierarchy/SkillsHierarchyManager';
import SkillsHierarchyManagerV2 from './components/SkillsHierarchy/SkillsHierarchyManagerV2';
// Practice Bites removed from UI
import GrammarDictionary from './components/GrammarDictionary/GrammarDictionary';
// Module Registry removed from UI
// Pipeline Registry removed from UI
import ContentManagementProject from './components/ContentManagementProject';
import { ContentManagementNav, ContentManagementSection } from './components/ContentManagementProject/ContentManagementNav';
import CourseManagementProject from './components/CourseManagementProject/CourseManagementProject';
import { CourseManagementNav, CourseManagementSection } from './components/CourseManagementProject/CourseManagementNav';
// Pipedrive Tasks removed from UI
import { hasProjectAccess, logAccessAttempt, getUserRole, PROJECT_ACCESS_RULES } from './utils/permissions';
import { fetchAuthSession } from 'aws-amplify/auth';
import { parseUrlParams, clearUrlParams, NavigationParams } from './utils/navigation';
import PromptStudio from './components/PromptStudio/PromptStudio';

type ProjectType = 'talk-to-tally-v2' | 'skills-manager' | 'grammar-dictionary' | 'prompt-studio' | 'content-management' | 'course-management';

/**
 * Filter project types based on user permissions
 */
const getFilteredProjectTypes = (userEmail: string | undefined) => {
  const allProjectTypes = [
    { id: 'talk-to-tally-v2', name: 'Talk To Tally V2', icon: '💬' },
    { id: 'prompt-studio', name: 'Prompt Studio', icon: '🧪' },
    { id: 'content-management', name: 'Content Management', icon: '📰' },
    { id: 'course-management', name: 'Course Management', icon: '📖' },
    { id: 'skills-manager', name: 'Skills DB Manager', icon: '🧠' },
    { id: 'grammar-dictionary', name: 'Grammar Dictionary', icon: '📚' },
  ];

  return allProjectTypes.filter(project => hasProjectAccess(project.id, userEmail));
};

const App: React.FC = () => {
  const [selectedProject, setSelectedProject] = useState<ProjectType | null>(null);
  const [contentManagementSection, setContentManagementSection] = useState<ContentManagementSection>('articles');
  const [courseManagementSection, setCourseManagementSection] = useState<CourseManagementSection>('courses');
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [showEmailOverride, setShowEmailOverride] = useState(false);
  const [manualEmail, setManualEmail] = useState('');
  const [urlParams, setUrlParams] = useState(parseUrlParams());

  // Handle URL parameter changes
  useEffect(() => {
    const params = parseUrlParams();
    setUrlParams(params);
    
    // Remove auto-select here; selection will be handled in AppContent with access checks
  }, [window.location.search]);

  return (
    <Login>
      {({ signOut, user }) => (
        <UserProvider user={user}>
          {(enhancedUser) => (
            <AppContent
              user={enhancedUser}
              signOut={signOut}
              selectedProject={selectedProject}
              setSelectedProject={setSelectedProject}
              contentManagementSection={contentManagementSection}
              setContentManagementSection={setContentManagementSection}
              courseManagementSection={courseManagementSection}
              setCourseManagementSection={setCourseManagementSection}
              notification={notification}
              setNotification={setNotification}
              isSidebarOpen={isSidebarOpen}
              setSidebarOpen={setSidebarOpen}
              showEmailOverride={showEmailOverride}
              setShowEmailOverride={setShowEmailOverride}
              manualEmail={manualEmail}
              setManualEmail={setManualEmail}
              urlParams={urlParams}
            />
          )}
        </UserProvider>
      )}
    </Login>
  );
};

// Component to handle user attribute fetching with proper hook usage
const UserProvider: React.FC<{ user: any; children: (enhancedUser: any) => React.ReactNode }> = ({ user, children }) => {
  const [fetchedAttributes, setFetchedAttributes] = useState<any>(null);
  const [attributesFetching, setAttributesFetching] = useState(false);
  const [fetchAttempted, setFetchAttempted] = useState(false);

  // Automatically fetch user attributes if they're missing from the user object
  useEffect(() => {
    const fetchMissingAttributes = async () => {
      // Skip if we already have attributes, are already fetching, or already tried
      if (user?.attributes || attributesFetching || fetchedAttributes || fetchAttempted) {
        console.log('🔍 [Auto-Fetch] Skipping fetch:', {
          hasAttributes: !!user?.attributes,
          isFetching: attributesFetching,
          hasFetched: !!fetchedAttributes,
          hasAttempted: fetchAttempted
        });
        return;
      }
      
      // Only fetch for users (not when user is null/undefined)
      if (!user?.username) {
        console.log('🔍 [Auto-Fetch] No username, skipping fetch');
        return;
      }
      
      console.log('🔄 [Auto-Fetch] User has no attributes, fetching from Cognito...');
      setAttributesFetching(true);
      setFetchAttempted(true);
      
      try {
        // Try to get the ID token and decode it for user attributes
        const session = await fetchAuthSession();
        console.log('✅ [Auto-Fetch] Successfully fetched auth session:', session);
        
        // The ID token should contain the user attributes
        const idToken = session.tokens?.idToken;
        if (idToken) {
          // Decode the ID token to get user attributes (they should be in the payload)
          const payload = idToken.payload;
          console.log('✅ [Auto-Fetch] ID Token payload:', payload);
          
          // Extract user attributes from the ID token payload
          const attributes = {
            email: payload.email,
            name: payload.name,
            given_name: payload.given_name,
            family_name: payload.family_name,
            picture: payload.picture,
            email_verified: payload.email_verified
          };
          
          console.log('✅ [Auto-Fetch] Extracted attributes from ID token:', attributes);
          setFetchedAttributes(attributes);
        } else {
          console.log('⚠️ [Auto-Fetch] No ID token found in session');
        }
      } catch (error) {
        console.error('❌ [Auto-Fetch] Failed to fetch user attributes:', error);
        console.error('❌ [Auto-Fetch] Error details:', {
          name: (error as Error)?.name,
          message: (error as Error)?.message,
          stack: (error as Error)?.stack
        });
        
        // If fetch fails, we still mark as attempted to prevent infinite retries
        console.log('⚠️ [Auto-Fetch] Will not retry automatic fetch');
      } finally {
        setAttributesFetching(false);
      }
    };

    fetchMissingAttributes();
  }, [user?.username]); // Only depend on username - not on state variables that change

  // Create enhanced user object with fetched attributes
  const enhancedUser = {
    ...user,
    attributes: fetchedAttributes || user?.attributes,
    _fetchedAttributes: fetchedAttributes,
    _attributesFetching: attributesFetching,
    _fetchAttempted: fetchAttempted
  };

  return <>{children(enhancedUser)}</>;
};

// Main app content component
const AppContent: React.FC<{
  user: any;
  signOut: () => void;
  selectedProject: ProjectType | null;
  setSelectedProject: (project: ProjectType | null) => void;
  contentManagementSection: ContentManagementSection;
  setContentManagementSection: (section: ContentManagementSection) => void;
  courseManagementSection: CourseManagementSection;
  setCourseManagementSection: (section: CourseManagementSection) => void;
  notification: any;
  setNotification: (notification: any) => void;
  isSidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  showEmailOverride: boolean;
  setShowEmailOverride: (show: boolean) => void;
  manualEmail: string;
  setManualEmail: (email: string) => void;
  urlParams: NavigationParams;
}> = ({
  user,
  signOut,
  selectedProject,
  setSelectedProject,
  contentManagementSection,
  setContentManagementSection,
  courseManagementSection,
  setCourseManagementSection,
  notification,
  setNotification,
  isSidebarOpen,
  setSidebarOpen,
  showEmailOverride,
  setShowEmailOverride,
  manualEmail,
  setManualEmail,
  urlParams
}) => {
  // Use the enhanced user with attributes
  const effectiveAttributes = user?.attributes;
  
  // Debug: Log the user object to understand its structure
  console.log('🔍 [Debug] Full user object:', JSON.stringify(user, null, 2));
  console.log('🔍 [Debug] User attributes (original):', user?.attributes);
  console.log('🔍 [Debug] Fetched attributes:', user?._fetchedAttributes);
  console.log('🔍 [Debug] Effective attributes (used):', effectiveAttributes);
  console.log('🔍 [Debug] User username:', user?.username);
  console.log('🔍 [Debug] Attributes fetching:', user?._attributesFetching);
  
  // Try multiple ways to extract email from Google OAuth
  const potentialEmails = [
    effectiveAttributes?.email,
    effectiveAttributes?.['custom:email'],
    effectiveAttributes?.preferred_username,
    user?.username,
    user?.signInDetails?.loginId
  ];
  
  console.log('🔍 [Debug] All potential email sources:', potentialEmails);
  
  // Find the first valid email address
  const userEmail = potentialEmails.find(email => 
    email && 
    typeof email === 'string' && 
    email.includes('@') && 
    !email.startsWith('Google_')
  );
  
  console.log('🔍 [Debug] Final extracted user email:', userEmail);
  console.log('🔍 [Debug] Email type:', typeof userEmail);
  
  // Extract display name (prefer actual name over Google ID)
  const displayName = effectiveAttributes?.name || 
                     effectiveAttributes?.given_name || 
                     effectiveAttributes?.nickname ||
                     (userEmail ? userEmail.split('@')[0] : null) ||
                     user?.username ||
                     'User';
  
  console.log('🔍 [Debug] Display name:', displayName);
  
  // Check if we need manual email override (OAuth attributes missing)
  const needsEmailOverride = !userEmail && user?.username?.startsWith('Google_');
  const needsManualRetry = !userEmail && user?._fetchAttempted && !user?._attributesFetching && !user?._fetchedAttributes;
  
  // Use manual email if provided, otherwise use extracted email
  const finalUserEmail = manualEmail || userEmail;
  console.log('🔍 [Debug] Final user email (with override):', finalUserEmail);
  console.log('🔍 [Debug] Needs manual retry:', needsManualRetry);
  
  // If no user email is available yet, show all non-restricted projects as fallback
  const userRole = finalUserEmail ? getUserRole(finalUserEmail) : 'standard';
  const projectTypes = getFilteredProjectTypes(finalUserEmail);
  const singleProjectId = projectTypes.length === 1 ? (projectTypes[0].id as ProjectType) : null;
  // Force a fresh TalkToTally mount when navigating back to it (ensures launch screen)
  const [tallyInstanceKey, setTallyInstanceKey] = useState<number>(0);

  // Enforce deep-link access: select only if permitted
  useEffect(() => {
    const pid = urlParams?.project as ProjectType | undefined;
    if (pid && !selectedProject) {
      // Defer access decision for restricted projects until email is available
      if (!finalUserEmail) {
        const isRestricted = Object.keys(PROJECT_ACCESS_RULES).includes(pid);
        if (isRestricted) {
          return; // avoid false "Access denied" toast while auth is still resolving
        }
      }
      if (hasProjectAccess(pid, finalUserEmail)) {
        setSelectedProject(pid);
      } else {
        setNotification({ message: 'Access denied: You do not have permission to access this project.', type: 'error' });
      }
    }
  }, [urlParams?.project, finalUserEmail, selectedProject]);

  // Guard against unauthorized selection (e.g., if selection changes elsewhere)
  useEffect(() => {
    // If email not yet available, postpone enforcement to avoid false negatives
    if (!finalUserEmail) return;
    if (selectedProject && !hasProjectAccess(selectedProject, finalUserEmail)) {
      setNotification({ message: 'Access denied: You do not have permission to access this project.', type: 'error' });
      setSelectedProject(null);
    }
  }, [selectedProject, finalUserEmail]);

  // Auto-select and hide sidebar when only one accessible project
  useEffect(() => {
    if (finalUserEmail && singleProjectId && !selectedProject) {
      setSelectedProject(singleProjectId);
    }
  }, [finalUserEmail, singleProjectId, selectedProject]);

  // Security check: If user tries to access a restricted project directly, prevent it
  const handleProjectSelection = (projectId: ProjectType) => {
    // If no user email available, prevent access to any restricted projects
    if (!finalUserEmail) {
      console.warn(`🚨 [Security] No user email available, preventing access to ${projectId}`);
      const isRestrictedProject = Object.keys(PROJECT_ACCESS_RULES).includes(projectId);
      if (isRestrictedProject) {
        setNotification({
          message: 'Authentication in progress. Please wait and try again.',
          type: 'error'
        });
        return;
      }
    }
    
    const hasAccess = hasProjectAccess(projectId, finalUserEmail);
    
    // Log the access attempt
    logAccessAttempt(projectId, finalUserEmail, hasAccess);
    
    if (!hasAccess) {
      console.warn(`🚨 [Security] Unauthorized access attempt to ${projectId} by ${finalUserEmail}`);
      setNotification({
        message: 'Access denied: You do not have permission to access this project.',
        type: 'error'
      });
      return;
    }
    setSelectedProject(projectId);
    if (projectId === 'talk-to-tally-v2') {
      // Ensure URL carries no auto-start/prompt studio params when entering from sidebar
      try { clearUrlParams(); } catch {}
      // Increment key so the component remounts cleanly each time we navigate back
      setTallyInstanceKey(prev => prev + 1);
    }
  };

  return (
    <div>
      <div className="min-h-screen bg-gray-50 font-poppins">
        {/* Top Navigation */}
        <nav className="bg-white border-b border-gray-200">
          <div className="px-4">
            <div className="grid grid-cols-3 items-center h-16">
              <div className="flex items-center space-x-4">
                <span className="text-gray-600">
                  Welcome, {displayName}
                </span>
                {!finalUserEmail && (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Loading Profile...
                  </span>
                )}
                {finalUserEmail && userRole !== 'standard' && (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    userRole === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {userRole.toUpperCase()}
                  </span>
                )}
              </div>
              <div className="text-center">
                <h1 className="text-xl font-semibold text-talaera-blue">Talaera AI Playground</h1>
              </div>
              <div className="text-right">
                <button 
                  onClick={signOut}
                  className="text-gray-500 hover:text-gray-700 text-sm"
                >
                  Sign out
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <div className="flex h-[calc(100vh-4rem)] relative">
          {/* Sidebar Toggle */}
          {projectTypes.length > 1 && !isSidebarOpen && (
            <button
              onClick={() => setSidebarOpen(true)}
              className="absolute left-0 top-4 z-10 p-2 bg-white rounded-r-lg shadow-md hover:bg-gray-50 focus:outline-none"
            >
              <svg
                className="w-5 h-5 text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          )}

          {/* Left Sidebar */}
          {projectTypes.length > 1 && (
            <div
              className={`${
                isSidebarOpen ? 'w-64' : 'w-0'
              } bg-white shadow-sm border-r flex flex-col transition-all duration-200 ease-in-out overflow-hidden`}
            >
            <div className="p-4 flex-grow">
              {selectedProject === 'course-management' ? (
                <CourseManagementNav
                  selectedSection={courseManagementSection}
                  onSelectSection={setCourseManagementSection}
                  onBack={() => { setSelectedProject(null); setCourseManagementSection('courses'); }}
                  onCollapse={() => setSidebarOpen(false)}
                />
              ) : selectedProject === 'content-management' ? (
                /* Content Management sub-navigation */
                <ContentManagementNav
                  selectedSection={contentManagementSection}
                  onSelectSection={setContentManagementSection}
                  onBack={() => { setSelectedProject(null); setContentManagementSection('articles'); }}
                  onCollapse={() => setSidebarOpen(false)}
                />
              ) : (
                <>
                  {/* Project Types header with close button */}
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Project Types
                    </h2>
                    <button
                      onClick={() => setSidebarOpen(false)}
                      className="p-1 hover:bg-gray-100 rounded-md focus:outline-none"
                    >
                      <svg
                        className="w-5 h-5 text-gray-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    </button>
                  </div>
                  <nav className="space-y-1">
                    {projectTypes.map((project) => (
                      <button
                        key={project.id}
                        onClick={() => handleProjectSelection(project.id as ProjectType)}
                        className={`w-full flex items-center px-3 py-2 text-sm rounded-md
                          ${selectedProject === project.id
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-700 hover:bg-gray-50'}`}
                      >
                        <span className="mr-3">{project.icon}</span>
                        {project.name}
                      </button>
                    ))}

                    {/* Show authentication loading notice */}
                    {!finalUserEmail && (
                      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-center">
                          <svg className="h-4 w-4 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div className="flex-1">
                            {user?._attributesFetching ? (
                              <>
                                <p className="text-xs font-medium text-yellow-800">Fetching Profile...</p>
                                <p className="text-xs text-yellow-700 mt-1">Loading your attributes from Cognito...</p>
                              </>
                            ) : needsManualRetry ? (
                              <>
                                <p className="text-xs font-medium text-yellow-800">Profile Load Failed</p>
                                <p className="text-xs text-yellow-700 mt-1">Could not load your profile automatically. Check console for errors.</p>
                                <button
                                  onClick={() => setShowEmailOverride(true)}
                                  className="mt-2 text-xs bg-yellow-600 text-white px-2 py-1 rounded hover:bg-yellow-700"
                                >
                                  Enter Email Manually
                                </button>
                              </>
                            ) : needsEmailOverride ? (
                              <>
                                <p className="text-xs font-medium text-yellow-800">Authentication Loading</p>
                                <p className="text-xs text-yellow-700 mt-1">Some projects may be hidden until your profile loads.</p>
                                <button
                                  onClick={() => setShowEmailOverride(true)}
                                  className="mt-2 text-xs bg-yellow-600 text-white px-2 py-1 rounded hover:bg-yellow-700"
                                >
                                  Manual Login
                                </button>
                              </>
                            ) : (
                              <>
                                <p className="text-xs font-medium text-yellow-800">Profile Loading</p>
                                <p className="text-xs text-yellow-700 mt-1">Some projects may be hidden until your profile loads.</p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </nav>
                </>
              )}
            </div>
            
            {/* Logo */}
            <div className="p-4 border-t">
              <img
                src="/logo.svg"
                alt="Talaera Logo"
                className="w-auto h-8 mx-auto"
              />
            </div>
            </div>
          )}

          {/* Main Panel */}
          <div className={`flex-1 ${selectedProject === 'talk-to-tally-v2' ? 'overflow-hidden' : 'overflow-auto'} bg-gray-50 transition-all duration-200 ease-in-out`}>
            {selectedProject ? (
              selectedProject === 'talk-to-tally-v2' ? (
                <TalkToTallyV2Project 
                  key={tallyInstanceKey}
                  user={{ displayName, email: finalUserEmail, attributes: effectiveAttributes }}
                  urlParams={urlParams}
                />
              ) : (
                <div className="p-8 mx-8">
                  {selectedProject === 'skills-manager' ? (
                  <SkillsHierarchyManagerV2 urlParams={urlParams} />
                ) : selectedProject === 'grammar-dictionary' ? (
                  <GrammarDictionary />
              ) : selectedProject === 'prompt-studio' ? (
                <PromptStudio currentUserEmail={finalUserEmail} />
                ) : selectedProject === 'content-management' ? (
                  <ContentManagementProject
                    activeSection={contentManagementSection}
                    onSectionChange={setContentManagementSection}
                  />
                ) : selectedProject === 'course-management' ? (
                  <CourseManagementProject
                    activeSection={courseManagementSection}
                  />
                ) : (
                  <div className="p-4 bg-white rounded-lg shadow">
                    <div className="text-gray-500">
                      This project type is not yet implemented
                    </div>
                  </div>
                )}
                </div>
              )
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                Select a project type to get started
              </div>
            )}
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
          />
        )}

        {/* Email Override Modal */}
        {showEmailOverride && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
              <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"></div>
              
              <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Email Verification Required
                  </h3>
                  <button
                    onClick={() => setShowEmailOverride(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Google authentication didn't provide your email address. Please enter your email to access restricted features:
                  </p>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={manualEmail}
                      onChange={(e) => setManualEmail(e.target.value)}
                      placeholder="Enter your email (e.g., mel@talaera.com)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex">
                      <svg className="h-5 w-5 text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-xs font-medium text-blue-800">Note:</p>
                        <p className="text-xs text-blue-700 mt-1">
                          This is a temporary workaround for OAuth configuration issues. Only authorized emails will have access to restricted features.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowEmailOverride(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (manualEmail.trim()) {
                        console.log('🔑 [Manual Override] User manually set email:', manualEmail);
                        setShowEmailOverride(false);
                        setNotification({
                          message: `Email set to: ${manualEmail}`,
                          type: 'success'
                        });
                      }
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                  >
                    Set Email
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
